import type { KeyboardEvent } from 'react';
import { Transforms, Range, Editor } from 'slate';
import { ReactEditor, useSlateStatic, useFocused } from 'slate-react';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import { ChatCategory } from '@/contexts/chat/scripts/hooks/useChats/types';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import genAvatar from '@/scripts/utils/genAvatar';
import Portal from '../portal';
import type { PortalProps, PortalRef } from '../../types';
import classes from './Portal.module.scss';
import { insertMention } from '.';

export default forwardRef<PortalRef, PortalProps>(function MentionPortal(
  { editableRef },
  ref
) {
  const {
    chatsState: { activatedChat },
    chatGroup: { getMembersByChatId },
  } = useChatContext();

  const editor = useSlateStatic();
  const focused = useFocused();

  const divRef = useRef<HTMLDivElement | null>(null);
  const [target, setTarget] = useState<Range | null>(null);
  const [groupIndex, setGroupIndex] = useState(0);
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [chatMembers, setChatMembers] = useState<ChatGroupMember[]>([]);
  const isGroupChat = useMemo(
    () => activatedChat?.category === ChatCategory.GROUP,
    [activatedChat?.category]
  );

  const allMembersGroup = useMemo(
    () => ({
      title: '',
      items: [
        {
          id: -1,
          user_id: '*',
          name: `All`,
          username: 'Notify all group members',
          avatar: genAvatar('All', {
            text: 'All',
          }),
        } as ChatGroupMember,
      ],
    }),
    []
  );

  const chatMembersGroup = useMemo(
    () => ({
      title: 'Members in chat',
      items: chatMembers,
    }),
    [chatMembers]
  );

  const groups = useMemo(
    () =>
      (search ? [chatMembersGroup] : [allMembersGroup, chatMembersGroup]).filter(
        ({ items }) => items.length
      ),
    [search, chatMembersGroup, allMembersGroup]
  );

  const refs = useRefs({ getMembersByChatId, activatedChat, isGroupChat });

  const select = useCallback(
    (args?: { groupIndex: number; index: number }) => {
      if (target) {
        Transforms.select(editor, target);
        const groupIndex_ = args?.groupIndex ?? groupIndex;
        const index_ = args?.index ?? index;
        const groupItem = groups[groupIndex_];
        const item = groupItem?.items[index_];
        if (item) {
          insertMention(editor, item);
        }
        setTarget(null);
      }
    },
    [target, editor, groupIndex, index, groups]
  );

  const onChange = useCallback(() => {
    const { selection } = editor;
    const { isGroupChat } = refs.current;

    if (isGroupChat && selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);
      const before = Editor.before(editor, start, { unit: 'line' });
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/@([^@\s]*)$/);

      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = /^(\s|$)/.test(afterText);

      if (beforeMatch && afterMatch) {
        const [match, $1] = beforeMatch;
        const range = Editor.range(
          editor,
          { ...start, offset: start.offset - match.length },
          start
        );

        setTarget(range);
        setSearch($1);
        setIndex(0);

        return;
      }
    }

    setTarget(null);
  }, [editor, refs]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (target && groups.length > 0) {
        const items = groups[groupIndex].items;
        let groupIndex_ = groupIndex;
        let index_ = index;

        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault();

            index_ = index + 1;

            if (index_ >= items.length) {
              groupIndex_ = (groupIndex + 1) % groups.length;
              index_ = 0;
            }

            setGroupIndex(groupIndex_);
            setIndex(index_);
            break;
          }
          case 'ArrowUp': {
            event.preventDefault();

            index_ = index - 1;

            if (index_ <= -1) {
              groupIndex_ = groupIndex - 1;
              if (groupIndex_ <= -1) {
                groupIndex_ = groups.length - 1;
              }
              index_ = groups[groupIndex_].items.length - 1;
            }

            setGroupIndex(groupIndex_);
            setIndex(index_);
            break;
          }
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            select();
            break;
          case 'Escape':
            event.preventDefault();
            setTarget(null);
            break;
        }
      }
    },
    [target, groups, groupIndex, index, select]
  );

  const clear = useCallback(() => {
    setTarget(null);
    setSearch('');
    setChatMembers([]);
  }, []);

  useImperativeHandle(ref, () => ({ onChange, onKeyDown }), [onChange, onKeyDown]);

  useLayoutEffect(() => {
    if (target && groups.length > 0 && divRef.current) {
      const popoverDom = divRef.current;
      const popoverDomRect = popoverDom.getBoundingClientRect();

      const selectionRange = ReactEditor.toDOMRange(editor, target);
      const selectionRect = selectionRange.getBoundingClientRect();

      const editableDom = editableRef.current!;
      const editableRect = editableDom.getBoundingClientRect();

      popoverDom.style.cssText = `
        top: ${selectionRect.top + window.scrollY - popoverDomRect.height}px;
        left: ${editableRect.left}px;
        width: ${editableRect.width}px;
      `;
    }
  }, [editor, target, editableRef, groups]);

  useLayoutEffect(() => {
    setGroupIndex(0);
    setIndex(0);
  }, [groups]);

  useLayoutEffect(() => {
    (async () => {
      let result: ChatGroupMember[] = [];
      const { activatedChat, getMembersByChatId, isGroupChat } = refs.current;
      if (isGroupChat && target && activatedChat) {
        const members = await getMembersByChatId(activatedChat.chat_id);
        if (search) {
          const reg = new RegExp(search, 'i');
          result = members.filter((m) => reg.test(m.name) || reg.test(m.username!));
        } else {
          result = members;
        }
      }
      setChatMembers(result);
    })();
  }, [search, target, refs]);

  useEffect(() => {
    const activeDom = divRef.current?.querySelector(`.${classes.active}`);
    if (activeDom) {
      activeDom.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [groupIndex, index, groups]);

  useEffect(() => {
    if (!focused) {
      clear();
    }
  }, [focused, clear]);

  return (
    target &&
    groups.length > 0 && (
      <Portal>
        <div
          ref={divRef}
          className={classes.popover}
          data-cy="mention-portal"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div className={classes.content}>
            {groups.map(({ title, items }, i) => (
              <div key={title} className={classes.group}>
                {title && <div className={classes.group_title}>{title}</div>}
                {items.map((item, j) => (
                  <div
                    className={cls(classes.item, {
                      [classes.active]: i === groupIndex && j === index,
                    })}
                    key={item.user_id}
                    onClick={() => {
                      select({
                        groupIndex: i,
                        index: j,
                      });
                    }}
                  >
                    <MyAvatar src={item.avatar} info={item}></MyAvatar>
                    <div className={classes.userInfo}>
                      <div className={classes.name}>
                        {item.user_id === '*'
                          ? `${item.name} (${chatMembers.length})`
                          : item.name}
                      </div>
                      {item.username && (
                        <div className={classes.username}>
                          {item.user_id === '*' ? item.username : `@${item.username}`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Portal>
    )
  );
});
